import React, { useEffect, useState } from 'react'
import { useTranslation } from "react-i18next"
import { scaleBand, scaleLinear, scaleOrdinal, max, sum } from 'd3';
import { isEmpty } from 'lodash';
import { useTheme } from '@material-ui/core';
import { laoChartColorPanel } from 'helpers/styleHelpers';
import ResponsiveWrapper from 'components/charts/ResponsiveWrapper';
import Axes from 'components/D3Charts/Axes';
import Bars from 'components/D3Charts/Bars';
import { Legends } from 'components/D3Charts/Pies';

const LaoBarChart = (props) => {
    const { t } = useTranslation();
    const {
        xKey, yKey, height, loading,
        themeType, yLabel, colorBy, colorPalette,
        legend, dx, showXAxisLabel, displayKey, setDetails, horizontal, ...rest
    } = props;
    const [data, setData] = useState([]);
    const [totalSum, setTotalSum] = useState(() => data.reduce((a, b) => a + b[yKey], 0));
    const keys = isEmpty(props.keys) ? [yKey] : props.keys;

    const colorPanel = keys.length > 2 ? colorPalette : laoChartColorPanel;

    useEffect(() => {
        setData(rest.data.map(d => ({ ...d, stateKey: d?.state, state: t(d?.state?.toLowerCase()), total: d[yKey] })))
        setTotalSum(rest.data.reduce((a, b) => a + b[yKey], 0))
    }, [rest.data]);

    let elementMargins = { top: 40, left: 80, right: 30, bottom: 30 };
    if (horizontal) {
        elementMargins = { top: 10, left: 120, right: 100, bottom: 20 }
    }

    const svgBoundaries = {
        width: rest.parentWidth,
        height: height - (legend ? 70 : 0)
    }

    let xScale = scaleBand()
        .padding(0.4)
        .paddingInner(0.3)
        .domain(data.map(d => d[xKey]))
        .range([elementMargins.left, svgBoundaries.width - elementMargins.right])

    let yScale = scaleLinear()
        .domain([0, max(data, d => sum(keys, k => +d[k]))])
        .nice()
        .range([svgBoundaries.height - elementMargins.bottom, elementMargins.top]);

    if (horizontal) {
        yScale = scaleBand()
            .padding(0.4)
            .paddingOuter(0.2)
            .paddingInner(0.2)
            .domain(data.map(d => d[xKey]))
            .range([elementMargins.top, svgBoundaries.height - elementMargins.bottom])

        xScale = scaleLinear()
            .range([elementMargins.left, svgBoundaries.width - elementMargins.right])
            .domain([0, max(data, d => sum(keys, k => +d[k]))]).nice();
    }

    const xAccess = d => d[xKey];

    let centerPos = [svgBoundaries.width / 2, svgBoundaries.height / 2];
    if (horizontal) {
        centerPos = [svgBoundaries.width / 2, svgBoundaries.height / 2 + elementMargins.top * 4];
    }

    let colors = scaleOrdinal().domain(keys).range(colorPanel);
    const newColorPanel = useTheme().palette[colorPalette];

    if (colorBy === "yKey") {
        colors = scaleOrdinal().domain(keys).range(newColorPanel);
    }

    if (colorBy === "xKey") {
        colors = scaleOrdinal().domain(data.map(d => d[xKey])).range(newColorPanel);
    }

    const NoDataText = () => {
        const text = loading ? "loading" : "no_data_found"
        return (
            <g transform={`translate(${centerPos})`} data-testid={"no-data-found"} >
                <text textAnchor="middle" data-testid="noData"
                    fill={themeType === "dark" ? "white" : "black"}>
                    {t(text)}
                </text>
            </g>
        )
    }

    return (
        <div data-testid='barChart'
            style={{
                minHeight: height + elementMargins.bottom,
                maxWidth: svgBoundaries.width + elementMargins.left
            }}>
            <svg width={svgBoundaries.width} height={svgBoundaries.height}>
                {

                    (!totalSum || loading) ? <NoDataText /> :
                        <g>
                            <Axes
                                wrapit={true}
                                scales={{ xScale, yScale }}
                                margins={elementMargins}
                                svgDimensions={svgBoundaries}
                                yLabel={yLabel}
                                showXAxisLabel={showXAxisLabel}
                                horizontal={horizontal}
                                dx={legend ? "-40%" : dx || "-50%"}
                            />
                            <Bars
                                scales={{ xScale, yScale }}
                                margins={elementMargins}
                                xKey={xKey}
                                data={data}
                                xAccessor={xAccess}
                                colors={colors}
                                colorBy={colorBy}
                                keys={keys}
                                themeType={themeType}
                                hideTopText={true}
                                setDetails={setDetails}
                                setDetailsData={rest.setDetailsData}
                                horizontal={horizontal}
                                subtype={rest?.subtype}
                                valueType={rest?.valueType}
                            />
                        </g>}
            </svg>
            {
                (!legend || !totalSum || loading)
                    ? null :
                    <Legends
                        {...rest}
                        data={horizontal ? rest.legendData : data}
                        xKey={xKey}
                        colorScale={colors}
                        setDetails={setDetails}
                        displayKey={displayKey}
                    // tooltipKey={tooltipKey}
                    // viewDetails={typeof viewDetails === "function"}
                    />
            }
        </div>
    )

}

export default ResponsiveWrapper(LaoBarChart)